import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactElement;
  requireAdmin?: boolean;
  requireRole?: 'Admin' | 'QA_Engineer' | 'Viewer';
}

// Role hierarchy: Admin > QA_Engineer > Viewer
const ROLE_LEVEL: Record<string, number> = {
  Viewer: 0,
  QA_Engineer: 1,
  Admin: 2,
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireRole,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', fontSize: '18px', color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Legacy requireAdmin support
  if (requireAdmin && user?.role !== 'Admin') {
    return <Navigate to="/projects" replace />;
  }

  // Role-level check: user must have at least the required role level
  if (requireRole) {
    const userLevel = ROLE_LEVEL[user?.role || 'Viewer'] ?? 0;
    const requiredLevel = ROLE_LEVEL[requireRole] ?? 0;
    if (userLevel < requiredLevel) {
      return <Navigate to="/projects" replace />;
    }
  }

  return children;
};

// Helper hook for role checks in components
export const useRBAC = () => {
  const { user } = useAuth();
  const role = user?.role || 'Viewer';
  const level = ROLE_LEVEL[role] ?? 0;

  return {
    role,
    isAdmin: role === 'Admin',
    isQAEngineer: level >= ROLE_LEVEL['QA_Engineer'],
    isViewer: role === 'Viewer',
    canWrite: level >= ROLE_LEVEL['QA_Engineer'],  // QA_Engineer and Admin can write
    canAdmin: role === 'Admin',
  };
};
